import { useMemo } from 'react';
import _ from 'lodash';
import { OrgRelationAccess } from 'utils/types';
import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { useGroupsQuery } from '../Groups/queries';
import { useUsersQuery } from '../Users/queries';
import { ResourceType } from 'utils/types';


/**
 * @description keys for the query cache for the user2groups relation
 */
export const user2groups = {
  all: ['user2groups'],
  users: () => [...user2groups.all, 'userGroups'],
  user: (userData) => [...user2groups.userGroups(), userData]
};

export const group2ug = {
  all: ['group2users'],
  groups: () => [...group2ug.all],
  group: (groupData) => [...group2ug.all, groupData]
};

export const org2users = {
  all: ['org2users'],
  orgs: () => [...org2users.all],
  org: (orgData) => [...org2users.all, orgData]
};

export const org2groups = {
  all: ['org2groups'],
  orgs: () => [...org2groups.all],
  org: (orgData) => [...org2groups.all, orgData]
};

export const users2org = {
  all: ['users2org'],
  users: () => [...users2org.all],
  user: (userData) => [...user2groups.all, userData]
};

export const groups2org = {
  all: ['groups2org'],
  groups: () => [...org2groups.all],
  group: (groupData) => [...org2groups.all, groupData]
};


export const org2dashboards = {
  all: ['org2dashboards'],
  orgs: () => [...org2dashboards.all],
  org: (orgData) => [...org2dashboards.all, orgData]
};

export const org2assets = {
  all: ['org2assets'],
  orgs: () => [...org2assets.all],
  org: (orgData) => [...org2assets.all, orgData]
};

export const org2alarms = {
  all: ['org2alarms'],
  orgs: () => [...org2alarms.all],
  org: (orgData) => [...org2alarms.all, orgData]
};

export const org2trees = {
  all: ['org2trees'],
  orgs: () => [...org2trees.all],
  org: (orgData) => [...org2trees.all, orgData]
};

export const org2models = {
  all: ['org2models'],
  orgs: () => [...org2models.all],
  org: (orgData) => [...org2models.all, orgData]
};

export const org2providers = {
  all: ['org2providers'],
  orgs: () => [...org2providers.all],
  org: (orgData) => [...org2providers.all, orgData]
};

export const org2providerClients = {
  all: ['org2providerClients'],
  orgs: () => [...org2providerClients.all],
  org: (orgData) => [...org2providerClients.all, orgData]
};

export const org2workflows = {
  all: ['org2workflows'],
  orgs: () => [...org2workflows.all],
  org: (orgData) => [...org2workflows.all, orgData]
};

export const org2notifications = {
  all: ['org2notifications'],
  orgs: () => [...org2notifications.all],
  org: (orgData) => [...org2notifications.all, orgData]
};

const order = [OrgRelationAccess.owner, OrgRelationAccess.manager, OrgRelationAccess.viewer];

export const createOrgResourceRelationData = (subjectType, orgId) => {
  return {
    resourceType: 'organization',
    optionalResourceId: orgId,
    optionalSubjectFilter: {
      subjectType: subjectType
    }
  };
};


export const createRelationQueryData = (resourceType, orgId) => {
  return {
    resourceType: resourceType,
    optionalSubjectFilter: {
      subjectType: 'organization',
      optionalSubjectId: orgId
    }
  };
};

export const createOrgOwnerRelationQueryData = (resourceType) => {
  return {
    resourceType: resourceType,
    optionalRelation: 'owner',
    optionalSubjectFilter: {
      subjectType: 'organization'
    }
  };
};

export const createOrgOwnerResourceRelationQueryData = (resourceType, resourceId) => {
  return {
    resourceType: resourceType,
    optionalResourceId: resourceId,
    optionalRelation: 'owner',
    optionalSubjectFilter: {
      subjectType: 'organization'
    }
  };
};

/**
 * @description hook to map relations to resources and verify if the resource is owned by the organization
 * @param {boolean} isLoading - if the query is loading
 * @param {Array} resources - the resources to map relations to
 * @param {Array} resourceRelations - the relations to map to the resources
 * @param {Array} ownedRelations - the owned relations to map to the resources. if not provided, all resources are considered owned
 * @param {string} resourceType - the type of the resource
 * @returns {Array} - the resources with the relations mapped to them
 * @example
 * const { isLoading, resources, resourceRelations, ownedRelations, resourceType } = props;
 * const relationData = useRelationData({ isLoading, resources, resourceRelations, ownedRelations, resourceType });
 *
 * return (
 *  <RelationTable
 *   orgId={orgId}
 *  resources={relationData}
 * isLoading={isLoading}
 * updateRelationsFn={updateRelations.mutate}
 * deleteRelationsFn={deleteRelations.mutate}
 * />
 * );
 */
export const useRelationData = ({ isLoading, resources, resourceRelations, ownedRelations, resourceType }) => {

  return useMemo(() => {
    if (isLoading) {
      return [];
    }
    const relations = _.map(_.uniqBy(_.orderBy(resourceRelations, data => _.indexOf(order, data.relation)), 'resourceId'), relation => {
      return {
        id: relation.resourceId,
        relation: relation.relation
      };
    });
    const ownedResources = ownedRelations ? _.filter(resources, resource => _.some(ownedRelations, { resourceId: resource.id })) : resources;
    return _.map(ownedResources, resource => {
      const relation = _.find(relations, { id: resource.id });
      return {
        ...resource,
        type: resourceType,
        relation: relation?.relation ?? OrgRelationAccess.none
      };
    });
  }, [isLoading, resourceRelations, resources, ownedRelations, resourceType]);
};

/**
 * @description hook to map user/group relations to orgs
 * @param {boolean} isLoading - if the query is loading
 * @param {Array} subjects - the subjects to map relations to
 * @param {Array} subjectRelations - the relations to map between subjects and orgs
 * @param {string} subujectType - the type of the resource
 * @returns {Array} - the subjects with the relations mapped to them
*/
export const useMemberRelations = ({ isLoading, subjects, subjectRelations, subjectType }) => {

  return useMemo(() => {
    if (isLoading) {
      return [];
    }
    const groupedRelations = _.groupBy(subjectRelations, 'subjectId');

    return _.map(subjects, subject => {
      const relations = _.map(_.get(groupedRelations, subject.id, []), r => r.relation);
      return {
        ...subject,
        type: subjectType,
        relations: relations
      };
    });
  }, [isLoading, subjectRelations, subjects, subjectType]);
};


export const useOrgGroupRelations = (orgId) => {
  const groupsQuery = useGroupsQuery();
  const api = useAPI();

  const groupRelationQuery = useQuery({
    queryKey: org2groups.org(createOrgResourceRelationData(ResourceType.Group, orgId)),
    queryFn: createQueryRequest(api.core.findRelationships),
    enabled: !!orgId
  });

  const groups = useMemberRelations({
    isLoading: groupRelationQuery.isLoading || groupsQuery.isLoading,
    subjects: groupsQuery.data,
    subjectRelations: groupRelationQuery.data,
    subjectType: ResourceType.Group
  });

  return {
    groups,
    groupRelationQuery,
    isLoading: groupRelationQuery.isLoading || groupsQuery.isLoading
  };
};

export const useOrgUserRelations = (orgId) => {
  const usersQuery = useUsersQuery();
  const api = useAPI();

  const userRelationQuery = useQuery({
    queryKey: org2users.org(createOrgResourceRelationData(ResourceType.User, orgId)),
    queryFn: createQueryRequest(api.core.findRelationships),
    enabled: !!orgId
  });

  const users = useMemberRelations({
    isLoading: userRelationQuery.isLoading || usersQuery.isLoading,
    subjects: usersQuery.data,
    subjectRelations: userRelationQuery.data,
    subjectType: ResourceType.User
  });

  return {
    users,
    userRelationQuery,
    isLoading: usersQuery.isLoading || userRelationQuery.isLoading
  };
};
